import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { ABOUT, TEAM, FAQS } from '../mock/mock';
import { PageHero } from './BuyABook';

const About = () => {
  return (
    <div className="fade-in">
      <PageHero title="About Us" />

      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16 text-center">
        <p className="font-body text-lg md:text-xl text-brand-brown/90 leading-relaxed">
          {ABOUT.intro}
        </p>
        <p className="font-serif text-2xl italic text-brand-brass mt-8">
          Leaving an indelible mark on the landscape of tomorrow.
        </p>
      </section>

      {/* Mission & Vision */}
      <section className="bg-sand/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-sand flex items-center justify-center">
                <Target className="h-6 w-6 text-brand-brass" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-brand-ink">Mission</h3>
            </div>
            <p className="text-brand-brown/85 leading-relaxed">{ABOUT.mission}</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-sand flex items-center justify-center">
                <Eye className="h-6 w-6 text-brand-brass" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-brand-ink">Vision</h3>
            </div>
            <p className="text-brand-brown/85 leading-relaxed">{ABOUT.vision}</p>
          </div>
        </div>
      </section>

      {/* Read clearly */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <img src={ABOUT.hero} alt="Read clearly" className="w-full h-80 object-cover rounded-2xl shadow-lg" />
          <div>
            <h2 className="font-serif text-4xl font-bold text-brand-ink">Read clearly. Think deeply.</h2>
            <div className="section-divider mt-4" />
            <p className="text-brand-brown/90 leading-relaxed mt-6 text-lg">
              Our distinctive idea was to focus exclusively on non-fiction, rather than trying to
              become another general-purpose bookstore carrying everything.
            </p>
            <Link to="/services">
              <Button className="mt-8 bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-12 px-8 gap-2">
                Our Services <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-sand/50">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 text-center">
          <h2 className="font-serif text-3xl font-bold text-brand-ink">Meet Our Team</h2>
          <div className="section-divider mx-auto mt-4 mb-10" />
          <div className="bg-white rounded-2xl p-8 shadow-md inline-block">
            <img
              src={TEAM.photo}
              alt={TEAM.name}
              className="h-40 w-40 rounded-full object-cover mx-auto shadow-lg ring-4 ring-sand"
            />
            <h3 className="font-serif text-2xl font-bold text-brand-ink mt-5">{TEAM.name}</h3>
            <p className="text-brand-brass font-sans">{TEAM.role}</p>
            <p className="text-brand-brown/80 max-w-md mt-4 leading-relaxed">{TEAM.bio}</p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl font-bold text-brand-ink">FAQs</h2>
          <div className="section-divider mx-auto mt-4" />
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white rounded-xl border border-brand-goldlight/50 px-5"
            >
              <AccordionTrigger className="font-sans text-left text-brand-ink hover:text-brand-brass">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-brown/85 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
};

export default About;
